import { Action, Card, ConfirmModal, OrderLineItem } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { ReactNode, useState } from 'react';
import { useCanEditShippingClaim } from '~/features/shippingClaimReview/hooks/useCanEditShippingClaim';
import { useShippingClaimLineItemDelete } from '~/features/shippingClaimReview/hooks/useShippingClaimLineItemDelete';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';

type ShipmentLineItem = CrewMerchantUi.OrderShipment['lineItems'][number];
type ClaimedLineItem = Extract<
  CrewMerchantUi.ShippingClaim['shippingClaimLineItems'][number],
  { kind: 'line_item' }
>;

function ShipmentLine({
  lineItem,
  claimed,
}: {
  lineItem: ShipmentLineItem;
  claimed?: ClaimedLineItem;
}) {
  const canEdit = useCanEditShippingClaim();
  const [showConfirm, setShowConfirm] = useState(false);
  const { mutate: removeFromClaim, isPending } =
    useShippingClaimLineItemDelete();

  return (
    <li className="flex items-start justify-between gap-2">
      <div className="flex-1">
        <OrderLineItem
          size="sm"
          title={lineItem.name}
          sku={lineItem.sku}
          src={lineItem.imgUrl}
          quantity={lineItem.quantity}
          options={lineItem.optionsFromPlatform}
        />
      </div>

      {claimed && canEdit && (
        <>
          <Action
            icon="remove"
            variant="ghost"
            accessibilityLabel="Remove from claim"
            onClick={() => setShowConfirm(true)}
          />
          <ConfirmModal
            title="Remove Line Item"
            prompt="Are you sure you want to remove this line item from the claim?"
            confirmText="Remove"
            cancelText="Cancel"
            variant="destructive"
            show={showConfirm}
            loading={isPending}
            onConfirm={() =>
              removeFromClaim(claimed, {
                onSettled: () => setShowConfirm(false),
              })
            }
            onCancel={() => setShowConfirm(false)}
          />
        </>
      )}
    </li>
  );
}

function LineGroup({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Card.Heading>{heading}</Card.Heading>
      <ul className="space-y-3">{children}</ul>
    </div>
  );
}

/** A shipment's lines split by whether they are on this claim, with claimed ones removable. */
export function ShipmentClaimLineItems({
  shipment,
}: {
  shipment: CrewMerchantUi.OrderShipment;
}) {
  const { shippingClaimLineItems } = useShippingClaim();

  const rows = shipment.lineItems.map((lineItem) => ({
    lineItem,
    claimed: shippingClaimLineItems.find(
      (li): li is ClaimedLineItem =>
        li.kind === 'line_item' && li.storeOrderLineItemId === lineItem.id,
    ),
  }));

  const partOfClaim = rows.filter((row) => row.claimed);
  const notPartOfClaim = rows.filter((row) => !row.claimed);

  return (
    <>
      {partOfClaim.length > 0 && (
        <LineGroup heading="Part of Claim">
          {partOfClaim.map(({ lineItem, claimed }) => (
            <ShipmentLine
              key={lineItem.id}
              lineItem={lineItem}
              claimed={claimed}
            />
          ))}
        </LineGroup>
      )}

      {notPartOfClaim.length > 0 && (
        <LineGroup heading="Not Part of Claim">
          {notPartOfClaim.map(({ lineItem }) => (
            <ShipmentLine key={lineItem.id} lineItem={lineItem} />
          ))}
        </LineGroup>
      )}
    </>
  );
}
